import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  FiPlay,
  FiSave,
  FiFolder,
  FiTerminal,
  FiEye,
  FiZap,
  FiShare2,
  FiSettings,
  FiCode,
  FiDownload,
  FiUpload,
  FiCopy,
  FiMaximize2,
  FiMinimize2,
  FiChevronLeft,
  FiChevronRight,
  FiMenu,
  FiX,
  FiSearch,
  
} from 'react-icons/fi'
import { VscRunAll, VscDebugStart, VscFiles } from 'react-icons/vsc'
import { IoMdColorWand, IoMdFlash } from 'react-icons/io'
import { FaFire } from 'react-icons/fa'
import ShareModal from '../ShareModal/ShareModal'
import toast from 'react-hot-toast'
import { RiFindReplaceLine } from 'react-icons/ri'

const EditorToolbar = ({
  currentFile,
  onSave,
  onRun,
  onToggleSidebar,
  sidebarOpen,
  onToggleTerminal,
  terminalOpen,
  onTogglePreview,
  previewOpen
}) => {
  const [showShareModal, setShowShareModal] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [replaceQuery, setReplaceQuery] = useState('')
  const [showSearch, setShowSearch] = useState(false)

  const handleRun = () => {
    toast.success('Running code... ⚡')
    onRun()
  }

  const handleSave = () => {
    if (currentFile) {
      toast.success('File saved! 💾')
      onSave(currentFile)
    }
  }

  const handleFormat = () => {
    toast.success('Code formatted! ✨')
    // Format code logic would go here
  }

  const handleDownload = () => {
    if (!currentFile) {
      toast.error('No file to download')
      return
    }

    const element = document.createElement('a')
    const file = new Blob([currentFile.content], { type: 'text/plain' })
    element.href = URL.createObjectURL(file)
    element.download = currentFile.name
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
    
    toast.success(`Downloaded ${currentFile.name}`)
  }

  const handleCopy = () => {
    if (!currentFile) {
      toast.error('No file to copy')
      return
    }

    navigator.clipboard.writeText(currentFile.content)
    toast.success('Code copied to clipboard! 📋')
  }

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  const handleSearch = () => {
    if (searchQuery) {
      toast.success(`Searching for: ${searchQuery}`)
      // Search logic would go here
    }
  }

  const handleReplace = () => {
    if (searchQuery && replaceQuery) {
      toast.success(`Replacing "${searchQuery}" with "${replaceQuery}"`)
      // Replace logic would go here
    }
  }

  const buttons = [
    {
      icon: <FiPlay />,
      label: 'Run',
      onClick: handleRun,
      color: 'from-green-500 to-emerald-500',
      shortcut: 'F5'
    },
    {
      icon: <FiSave />,
      label: 'Save',
      onClick: handleSave,
      color: 'from-blue-500 to-cyan-500',
      shortcut: 'Ctrl+S'
    },
    {
      icon: <IoMdColorWand />,
      label: 'Format',
      onClick: handleFormat,
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: <FiShare2 />,
      label: 'Share',
      onClick: () => setShowShareModal(true),
      color: 'from-indigo-500 to-purple-500'
    },
    {
      icon: <FiDownload />,
      label: 'Download',
      onClick: handleDownload,
      color: 'from-amber-500 to-orange-500'
    },
    {
      icon: <FiCopy />,
      label: 'Copy',
      onClick: handleCopy,
      color: 'from-gray-500 to-gray-600'
    }
  ]

  const toggleButtons = [
    {
      icon: sidebarOpen ? <FiChevronLeft /> : <FiChevronRight />,
      label: sidebarOpen ? 'Hide Sidebar' : 'Show Sidebar',
      active: sidebarOpen,
      onClick: onToggleSidebar
    },
    {
      icon: <FiTerminal />,
      label: terminalOpen ? 'Hide Terminal' : 'Show Terminal',
      active: terminalOpen,
      onClick: onToggleTerminal
    },
    {
      icon: <FiEye />,
      label: previewOpen ? 'Hide Preview' : 'Show Preview',
      active: previewOpen,
      onClick: onTogglePreview
    },
    {
      icon: isFullscreen ? <FiMinimize2 /> : <FiMaximize2 />,
      label: isFullscreen ? 'Exit Fullscreen' : 'Fullscreen',
      active: isFullscreen,
      onClick: toggleFullscreen
    }
  ]

  return (
    <>
      <div className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        {/* Main Toolbar */}
        <div className="px-4 py-2 flex items-center justify-between">
          {/* Left Side */}
          <div className="flex items-center space-x-2">
            <button
              onClick={onToggleSidebar}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              title={sidebarOpen ? 'Hide Sidebar' : 'Show Sidebar'}
            >
              {sidebarOpen ? <FiChevronLeft /> : <FiChevronRight />}
            </button>

            {currentFile && (
              <div className="flex items-center space-x-3 ml-4">
                <div className="flex items-center space-x-2">
                  <FaFire className="text-primary-600 dark:text-primary-400 animate-pulse" />
                  <span className="font-medium text-gray-900 dark:text-white">
                    {currentFile.name}
                  </span>
                </div>
                <div className="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 text-xs font-medium rounded">
                  {currentFile.language?.toUpperCase() || 'TEXT'}
                </div>
              </div>
            )}
          </div>

          {/* Center - Action Buttons */}
          <div className="hidden md:flex items-center space-x-2">
            {buttons.map((button, index) => (
              <motion.button
                key={index}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={button.onClick}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg bg-gradient-to-r ${button.color} text-white font-medium hover:shadow-lg transition-all`}
              >
                {button.icon}
                <span>{button.label}</span>
                {button.shortcut && (
                  <span className="text-xs opacity-80">{button.shortcut}</span>
                )}
              </motion.button>
            ))}
          </div>

          {/* Right Side - Toggle Buttons */}
          <div className="flex items-center space-x-2">
            {/* Search Toggle */}
            <button
              onClick={() => setShowSearch(!showSearch)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              title="Search & Replace"
            >
              <FiSearch />
            </button>

            {/* Toggle Buttons */}
            {toggleButtons.slice(1).map((button, index) => (
              <button
                key={index}
                onClick={button.onClick}
                className={`p-2 rounded-lg transition-colors ${
                  button.active
                    ? 'bg-primary-500 text-white'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
                title={button.label}
              >
                {button.icon}
              </button>
            ))}
          </div>
        </div>

        {/* Search Bar */}
        {showSearch && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50"
          >
            <div className="flex items-center space-x-4">
              <div className="flex-1 flex items-center space-x-4">
                <div className="flex-1">
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search in file..."
                      className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 text-sm"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="relative">
                    <RiFindReplaceLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={replaceQuery}
                      onChange={(e) => setReplaceQuery(e.target.value)}
                      placeholder="Replace with..."
                      className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 text-sm"
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium"
                >
                  Search
                </button>
                <button
                  onClick={handleReplace}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium"
                >
                  Replace
                </button>
                <button
                  onClick={() => setShowSearch(false)}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
                >
                  <FiX />
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Mobile Action Buttons */}
        <div className="md:hidden px-4 py-2 border-t border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between overflow-x-auto space-x-2 pb-2">
            {buttons.map((button, index) => (
              <button
                key={index}
                onClick={button.onClick}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg bg-gradient-to-r ${button.color} text-white text-sm font-medium flex-shrink-0`}
              >
                {button.icon}
                <span>{button.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
          file={currentFile}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </>
  )
}

export default EditorToolbar